import { useRef, useEffect } from 'react';
import supabase from './supabase';
import {
  formatDateToYYYYMMDD,
  sanitizeFileName,
  formatSearchStringForFts,
  cityTagSuffix,
  bannerMatchesCity,
} from './data-factory';

const NOT_ARCHIVED_FILTER = 'is_archived.is.false,is_archived.is.null';

// Make an API request to `/api/{path}`
export async function apiRequest(path, method = 'GET', data) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session ? session.access_token : undefined;

  return fetch(`/api/${path}`, {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: data ? JSON.stringify(data) : undefined,
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === 'error') {
        // Automatically signout user if accessToken is no longer valid
        if (response.code === 'auth/invalid-user-token') {
          supabase.auth.signOut();
        }

        throw new CustomError(response.code, response.message);
      } else {
        return response.data;
      }
    });
}

/**
 * Submit an admin (gallery claim) application.
 * @param {Object} orgData - orgMode, orgId, orgName, orgBio
 * @param {Object} infoData - contactName, contactPhone, additionalInfo, proofFiles
 * @returns {Promise<Object>}
 */
export function submitAdminApplication(orgData, infoData) {
  return apiRequest('gallery-claim-request', 'POST', {
    orgMode: orgData.orgMode,
    orgId: orgData.orgId,
    orgName: orgData.orgName,
    orgBio: orgData.orgBio,
    contactName: infoData.contactName,
    contactPhone: infoData.contactPhone || null,
    additionalInfo: infoData.additionalInfo,
    proofPaths: infoData.proofFiles?.map(f => f.path) || [],
  });
}

// Make an API request to any external URL
export function apiRequestExternal(url, method = 'GET', data) {
  return fetch(url, {
    method: method,
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: data ? JSON.stringify(data) : undefined,
  }).then(response => response.json());
}

// Create an Error with custom message and code
export function CustomError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

// Hook that returns previous value of state
export function usePrevious(state) {
  const ref = useRef();
  useEffect(() => {
    ref.current = state;
  });
  return ref.current;
}

// Format search string to be used in textSearch supabase query.
// In Postgres, the tsquery type does not allow unescaped whitespace
// because it treats whitespace as a separator between lexemes (words).
export function formatSearchString(searchString) {
  return searchString.includes(' ') ? searchString.split(' ').join(' & ') : searchString;
}

/**
 * Public URL for an uploaded profile photo
 */
export function getPublicProfilePhotoUrl(userId, cacheBust = Date.now()) {
  if (!userId) return null;
  return `https://api.showrunner.co/storage/v1/object/public/uploads/${userId}/profilephoto/profilephoto.jpg?v=${cacheBust}`;
}

/**
 * a function to get the user's profile photo from the database
 * using the user's id or the user's metadata. Has three fallbacks:
 * 1. If the user has uploaded a profile photo, get it from the uploads storage.
 * 2. If the user has not uploaded a profile photo, get the user's picture from the user's metadata.
 * @param {*} id
 * @param {*} userMetadata
 * @returns
 */
export async function getProfilePhoto(id) {
  if (!id) {
    return;
  }

  const timestamp = new Date().getTime();
  const { data, error } = await supabase.storage
    .from('uploads')
    .download(`${id}/profilephoto/profilephoto.jpg`);

  if (error) {
    console.error(error, 'Then fallback to other sources');
  }

  if (data && !error) {
    return getPublicProfilePhotoUrl(id, timestamp);
  } else {
    const user = await getUser(id);
    return user.picture;
  }
}

/**
 * First initial for seen-by avatars
 */
export function getSeenByInitialsFallback(user) {
  const label = (user.username || '').trim() || 'Unknown';
  return label[0].toUpperCase();
}

/**
 * a function to get the cover photo for a list
 * @param {*} id
 * @returns
 */
export async function getFeaturedListCoverPhoto(id) {
  if (!id) {
    return;
  }

  const { data, error } = await supabase.storage
    .from('featured-list-covers')
    .download(`${id}/coverphoto/coverphoto.jpg`);

  if (error) {
    console.error(error, 'Could not retrieve cover photo');
  } else if (data) {
    const url = `https://api.showrunner.co/storage/v1/object/public/featured-list-covers/${id}/coverphoto/coverphoto.jpg`;
    return url;
  }
}

/**
 * Upload a profile photo to the user's uploads folder storage.
 * If a file already exists, update it, otherwise upload a new one.
 * @param {*} file
 * @param {*} userId
 * @returns
 */
export async function uploadProfilePhoto(file, userId) {
  const { data, error } = await supabase.storage.from('uploads').list(`${userId}/profilephoto`);

  if (error) {
    throw error;
  }

  // If there is already a file, update it, otherwise upload a new one
  // This is to avoid creating multiple files with the same name.
  if (data.length) {
    const new_file = new File([file], `profilephoto.jpg`, { type: file.type });
    const { data, error } = await supabase.storage
      .from('uploads')
      .update(`${userId}/profilephoto/profilephoto.jpg`, new_file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) {
      throw error;
    } else {
      return data;
    }
  } else {
    const newFileName = 'profilephoto.jpg';
    const { uploadData, uploadError } = await supabase.storage
      .from('uploads')
      .upload(`${userId}/profilephoto/${newFileName}`, file);
    if (uploadError) {
      throw uploadError;
    } else {
      return uploadData;
    }
  }
}

/**
 * Upload a gallery claim proof file to storage.
 * @param {string} userId
 * @param {File} file
 * @returns {Promise<{path: string, name: string}>}
 */
export async function uploadGalleryClaimProof(userId, file) {
  const path = `${userId}/gallery_claim/${Date.now()}-${sanitizeFileName(file.name)}`;
  const { error } = await supabase.storage.from('gallery-claim-proofs').upload(path, file);

  if (error) {
    throw error;
  }

  return { path, name: file.name };
}

/**
 * a function to get shows from the database based on sub-filters.
 * Multi-select filters are passed as sets.
 * @param {*} param0
 * @returns
 */
export async function getShows({
  open,
  sortedByRelevance,
  selectedMediums,
  selectedLocation,
  selectedSubject,
  selectedStyles,
  showType,
  textSearch,
  city = 'New York',
}) {
  const today = formatDateToYYYYMMDD(new Date());
  const twoWeeksFromNow = formatDateToYYYYMMDD(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));

  let query = supabase
    .from('shows')
    .select(
      'show_id, show_title, display_title, start_date, end_date, opening_date, opening_start_time, opening_end_time, image_urls, artist_names, featured, recommended, label, top_tags, show_type, venues!inner(org_id, neighborhood, venue_name, latitude, longitude, address1)'
    )
    .eq('venues.city', city)
    .or(NOT_ARCHIVED_FILTER)
    .eq('duplicate', 0);

  // if there is a text search, search for shows with that text search
  // if there is no text search, default to shows that haven't ended yet
  if (textSearch) {
    query
      .textSearch('search_terms', formatSearchString(textSearch))
      .order('opening_sort', { ascending: false });
  } else {
    query.gte('end_date', today);
    query.order('home_sort');
  }

  if (selectedLocation.size > 0) {
    query.in('venues.neighborhood', [...selectedLocation]);
  }

  if (selectedMediums.size > 0) {
    query.overlaps('show_medium', [...selectedMediums]);
  }

  if (selectedStyles.size > 0) {
    query.overlaps('show_style', [...selectedStyles]);
  }

  if (selectedSubject.size > 0) {
    query.overlaps('show_subject_matter', [...selectedSubject]);
  }

  if (showType.size > 0) {
    query.in('show_type', [...showType]);
  }

  let timeConditions = [];
  if (open.has('On View')) {
    query = query.lt('start_date', today).gte('end_date', today);
    query.order('home_sort');
  }
  if (open.has('Opening Today')) {
    timeConditions.push(`start_date.eq.${today}`);
    timeConditions.push(`opening_date.eq.${today}`);
    const combinedtimeConditions = timeConditions.join(',');
    query = query.or(combinedtimeConditions);
    query.order('home_sort');
  }
  if (open.has('Opening Soon')) {
    timeConditions.push(`start_date.gte.${today}`);
    const combinedtimeConditions = timeConditions.join(',');
    query = query.or(combinedtimeConditions);
    query.order('opening_sort', { ascending: true });
  }
  if (open.has('Closing Soon')) {
    query.gte('end_date', today);
    timeConditions.push(`end_date.lte.${twoWeeksFromNow}`);
    const combinedtimeConditions = timeConditions.join(',');
    query = query.or(combinedtimeConditions);
    query.order('closing_sort', { ascending: true });
  }

  const { data, error } = await query;
  // console.debug(data);

  if (error) {
    throw error;
  }

  return data;
}

/** Shows with `show_type = 'Future Fair'` (floor map list + booth artwork merge). */
export async function getFutureFairShows() {
  const today = formatDateToYYYYMMDD(new Date());

  const { data, error } = await supabase
    .from('shows')
    .select(
      '*, venues!inner(neighborhood, venue_name, city, address1, phone, latitude, longitude, image_urls)'
    )
    .eq('show_type', 'Future Fair')
    .eq('duplicate', 0)
    .or(NOT_ARCHIVED_FILTER)
    .gte('end_date', today)
    .order('show_title', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
}

// a function to get a single show by its id
export async function getShowById(showId) {
  const { data, error } = await supabase
    .from('shows')
    .select(
      '*, venues!inner(org_id, neighborhood, venue_name, city, address1, phone, latitude, longitude, image_urls )'
    )
    .eq('show_id', showId);

  if (error) {
    throw error;
  }

  return data;
}

// CRM/admin-added shows are reserved show_id 3,000,000-3,999,999.
const CRM_SHOW_ID_RANGE_START = 3000000;
const CRM_SHOW_ID_RANGE_END = 4000000;

/**
 * Save or update a show in the database.
 * @param {Object} show
 * @param {boolean} isNewShow
 * @returns {Promise<void>}
 */
export async function postShowData(show, isNewShow = true) {
  if (isNewShow) {
    const { data: maxRow, error: maxError } = await supabase
      .from('shows')
      .select('show_id')
      .gte('show_id', CRM_SHOW_ID_RANGE_START)
      .lt('show_id', CRM_SHOW_ID_RANGE_END)
      .order('show_id', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (maxError) {
      console.error('Error finding next show_id:', maxError);
      throw maxError;
    }
    const nextShowId = maxRow ? maxRow.show_id + 1 : CRM_SHOW_ID_RANGE_START;
    if (nextShowId >= CRM_SHOW_ID_RANGE_END) {
      throw new Error(
        `CRM show_id range exhausted (${CRM_SHOW_ID_RANGE_START}-${CRM_SHOW_ID_RANGE_END - 1})`
      );
    }

    const { data, error } = await supabase
      .from('shows')
      .insert([{ ...show, show_id: nextShowId, show_type: 'Gallery', duplicate: 0, locked: 1 }])
      .select();

    if (error) {
      console.error('Error saving show:', error);
      throw error;
    }

    return data?.[0];
  } else {
    const { id, ...showData } = show;
    const { error } = await supabase
      .from('shows')
      .update({ ...showData, locked: 1 })
      .eq('show_id', id);
    if (error) {
      console.error('Error updating show:', error);
      throw error;
    }
  }
}

/**
 * Save or update a venue in the database.
 * @param {Object} venue - The venue data to save.
 * @returns {Promise<void>}
 */
export async function postVenueData(venue) {
  const { error } = await supabase.from('venues').upsert(
    [
      {
        ...venue,
      },
    ],
    { onConflict: 'venue_id' }
  );

  if (error) {
    console.error('Error saving venue:', error);
    throw error;
  }
}

/**
 * Create a new organization in the database.
 * @param {Object} org - The org data to insert.
 * @returns {Promise<Object>} The created org row.
 */
export async function createOrg(org) {
  const DASHBOARD_ORG_ID_START = 1_000_000;

  const { data: maxRow, error: maxError } = await supabase
    .from('organization')
    .select('org_id')
    .gte('org_id', DASHBOARD_ORG_ID_START)
    .order('org_id', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (maxError) {
    console.error('Error fetching max dashboard org id:', maxError);
    throw maxError;
  }

  const nextId = maxRow ? maxRow.org_id + 1 : DASHBOARD_ORG_ID_START;

  const { data, error } = await supabase
    .from('organization')
    .insert([{ ...org, org_id: nextId }])
    .select();

  if (error) {
    console.error('Error creating org:', error);
    throw error;
  }
  return data?.[0];
}

/**
 * Create a new venue in the database.
 * @param {Object} venue - The venue data to insert.
 * @returns {Promise<Object>} The created venue row.
 */
export async function createVenue(venue) {
  const { data, error } = await supabase.from('venues').insert([venue]).select();
  if (error) {
    console.error('Error creating venue:', error);
    throw error;
  }
  return data?.[0];
}

/**
 * Toggle is_archived for a venue in the database.
 * @param {number} venueId - The ID of the venue to toggle.
 * @param {boolean} currentValue - The current value of is_archived.
 * @returns {Promise<void>}
 */
export async function toggleVenueArchived(venueId, currentValue) {
  const { error } = await supabase.rpc('set_venue_archived', {
    p_venue_id: venueId,
    p_archived: !currentValue,
  });
  if (error) throw error;
}

/**
 * Combine two venues: move all shows from archiveVenueId to keepVenueId,
 * record the alteration so the ingestion pipeline can remap future shows,
 * and archive the old venue. Runs as one transaction in the `combine_venues`
 * Postgres function (defined in Supabase).
 * @param {number} keepVenueId - The venue to keep.
 * @param {number} archiveVenueId - The venue to merge from and archive.
 * @returns {Promise<void>}
 */
export async function combineVenues(keepVenueId, archiveVenueId) {
  const { error } = await supabase.rpc('combine_venues', {
    p_keep_venue_id: keepVenueId,
    p_archive_venue_id: archiveVenueId,
  });
  if (error) throw error;
}

/**
 * Toggle is_archived for a show in the database.
 * @param {number} showId - The ID of the show to toggle.
 * @param {boolean} currentValue - The current value of is_archived.
 * @returns {Promise<void>}
 */
export async function toggleIsArchived(showId, currentValue) {
  const { error } = await supabase
    .from('shows')
    .update({ is_archived: !currentValue, locked: 1 })
    .eq('show_id', showId);

  if (error) {
    throw error;
  }
}

/**
 * Combine two shows that are duplicates.
 * @param {number} keepShowId - The show to keep.
 * @param {number} mergeShowId - The show to merge from and hide.
 * @returns {Promise<void>}
 */
export async function combineShows(keepShowId, mergeShowId) {
  const { error: chainError } = await supabase
    .from('shows')
    .update({ merged_into: keepShowId, locked: 1 })
    .eq('merged_into', mergeShowId);
  if (chainError) throw chainError;

  const { error: mergeError } = await supabase
    .from('shows')
    .update({ is_archived: true, merged_into: keepShowId, locked: 1 })
    .eq('show_id', mergeShowId);
  if (mergeError) throw mergeError;
}

/**
 * From a list of show IDs, get the shows from the database.
 * @param {*} showIds
 * @returns
 */
export async function getShowsByIDs(showIds) {
  const { data, error } = await supabase
    .from('shows')
    .select(
      '*, venues!inner(org_id, neighborhood, venue_name, city, address1, phone, latitude, longitude, image_urls )'
    )
    .in('show_id', showIds)
    .or(NOT_ARCHIVED_FILTER);

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Get a page of audit log entries for an org, newest first. Readable by superadmins only (RLS).
 * @param {number} orgID
 * @param {number|null} [beforeId] - id of the last entry already loaded, for paging
 * @param {number} [limit]
 * @returns {Promise<Object[]>}
 */
export async function getAuditLogByOrgID(orgID, beforeId = null, limit = 50) {
  // paged by id, not occurred_at: rows written in one statement share the same timestamp
  let query = supabase
    .from('audit_log')
    .select('*')
    .eq('org_id', orgID)
    .order('id', { ascending: false })
    .limit(limit);
  if (beforeId) query = query.lt('id', beforeId);

  const { data, error } = await query;
  if (error) {
    throw error;
  }

  return data;
}

/**
 * From an org ID, get the corresponding venues from the database.
 * @param {*} orgID
 * @returns
 */
export async function getVenuesByOrgID(orgID) {
  const { data, error } = await supabase.from('venues').select('*').eq('org_id', orgID);

  if (error) {
    throw error;
  }

  return data;
}

/**
 * From a venue ID, get the corresponding shows from the database.
 * @param {*} venueID
 * @returns
 */
export async function getShowsByVenueID(venueID) {
  const { data, error } = await supabase
    .from('shows')
    .select('*, venues!inner(neighborhood, venue_name, city, address1, phone, latitude, longitude, image_urls)')
    .eq('venue_id', venueID)
    .eq('duplicate', 0)
    .order('start_date', { ascending: false });

  if (error) throw error;

  return data || [];
}

/**
 * Get every show at any of an org's venues (archived venues included), in one
 * paged query instead of one request per venue.
 * @param {*} orgID
 * @returns {Promise<Object[]>}
 */
export async function getShowsByOrgID(orgID) {
  const PAGE_SIZE = 1000;
  const shows = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('shows')
      .select('*, venues!inner(org_id, neighborhood, venue_name, city, address1, phone, latitude, longitude, image_urls)')
      .eq('venues.org_id', orgID)
      .eq('duplicate', 0)
      .order('show_id', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);

    if (error) throw error;
    shows.push(...(data ?? []));
    if (!data || data.length < PAGE_SIZE) return shows;
  }
}

/**
 * From a venue text query, get the partial matching venues from the database.
 * @param {*} venueQuery
 * @returns
 */
export async function venueSearch(venueQuery) {
  const rpcName = 'search_galleries_by_prefix';
  const searchText = formatSearchStringForFts(venueQuery);

  const { data, error } = await supabase.rpc(rpcName, {
    prefix: searchText,
  });

  if (error) {
    console.error('Error searching venues:', error);
    return [];
  }

  return data;
}

/**
 * From an org text query, get the partial matching orgs from the database.
 * @param {*} orgQuery
 * @returns
 */
export async function orgSearch(orgQuery) {
  const rpcName = 'search_orgs_by_prefix';
  const searchText = formatSearchStringForFts(orgQuery);

  const { data, error } = await supabase.rpc(rpcName, {
    prefix: searchText,
  });

  if (error) {
    console.error('Error searching orgs:', error);
    return [];
  }

  return data;
}

/**
 * From an org ID, get the org data from the database.
 * @param {*} orgID
 * @returns
 */
export async function getOrgByID(orgID) {
  const { data, error } = await supabase
    .from('organization')
    .select('*')
    .eq('org_id', orgID)
    .single();

  if (error) {
    console.error('Error fetching org:', error);
    return null;
  }

  return data;
}

export async function updateOrgInfo(orgId, { bio, website, instagram, contact_email, phone }) {
  const { error } = await supabase
    .from('organization')
    .update({ bio, website, instagram, contact_email, phone })
    .eq('org_id', orgId);

  if (error) {
    console.error('Error updating org info:', error);
    throw error;
  }
}

/**
 * Get the previe data for a given show Id.
 * @param {*} showId
 * @returns
 */
export async function getPreviewData(showId) {
  const { data, error } = await supabase.from('preview_images').select('*').eq('show_id', showId);

  if (error) {
    throw error;
  }

  return data;
}

/**
 * A function to get saved lists by the owner's ID.
 * @param {*} owner
 * @returns
 */
export async function getListsByOwner(owner) {
  const { data, error } = await supabase.from('saved_lists').select('*').eq('owner', owner);
  if (error) {
    throw error;
  }
  const listsWithFirstShow = await Promise.all(
    data.map(async list => {
      if (list.shows.length > 0) {
        const res = await supabase.from('shows').select('*').eq('show_id', list.shows[0]);

        if (res.error) {
          throw res.error;
        }

        return {
          ...list,
          first_show_data: res.data[0],
        };
      }

      return list;
    })
  );

  return listsWithFirstShow;
}

/**
 * a function to get saved lists by the username
 * @param {*} username
 * @returns
 */
export async function getListsByUsername(username) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('username', username)
    .single();
  if (error) {
    throw error;
  }

  try {
    const userLists = await getListsByOwner(data.id);
    return { userData: data, userLists };
  } catch (error) {
    throw error;
  }
}

/**
 * Get user obejct by uid.
 * @param {*} uid
 * @returns
 */
export async function getUser(uid) {
  const { data, error } = await supabase
    .from('users')
    .select('picture, name, username, about, created_at')
    .eq('id', uid)
    .single();

  if (error) {
    throw error;
  }

  return data;
}

// a function to get a single list by its id.
export async function getListById(listId) {
  const { data, error } = await supabase.from('saved_lists').select('*').eq('id', listId).single();
  if (error) {
    throw error;
  }

  if (data.shows.length > 0) {
    const res = await supabase
      .from('shows')
      .select('*, venues!inner(neighborhood, venue_name, longitude, latitude)')
      .in('show_id', data.shows);

    if (res.error) {
      throw res.error;
    }

    return {
      ...data,
      shows_data: res.data,
    };
  }

  // if there are no shows in the list,
  // return the list with an empty shows_data array
  return { ...data, shows_data: [] };
}

export async function getUsernameByList(listOwnerId) {
  const { data, error } = await supabase
    .from('users')
    .select('username')
    .eq('id', listOwnerId)
    .single();

  if (error) {
    throw error;
  }
  return data?.username;
}

export async function searchHeadTypeSeacrh(searchString) {
  let query = supabase
    .from('shows')
    .select('*, venues!inner(neighborhood, venue_name, latitude, longitude, address1)')
    .textSearch('search_terms', formatSearchString(searchString))
    .order('opening_sort', { ascending: false });

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data;
}

// a function to get list objects from the lists tables that match owner id
export async function getFeaturedLists() {
  const { data, error } = await supabase.from('saved_lists').select('*').eq('is_featured', true);

  if (error) {
    throw error;
  }

  const listsWithCoverPhotos = await Promise.all(
    data.map(async list => {
      if (list.shows.length > 0) {
        const coverPhoto = await getFeaturedListCoverPhoto(list.id);

        if (coverPhoto) {
          return {
            ...list,
            cover_photo: coverPhoto,
          };
        }

        const firstShowPhoto = await supabase
          .from('shows')
          .select('*')
          .eq('show_id', list.shows[0]);

        if (firstShowPhoto.error) {
          throw firstShowPhoto.error;
        }

        return {
          ...list,
          cover_photo: firstShowPhoto.data[0]?.image_urls[0],
        };
      }

      return list;
    })
  );

  const listsWithOwnerData = await Promise.all(
    listsWithCoverPhotos.map(async list => {
      if (list.shows.length > 0) {
        const res = await supabase.from('users').select('*').eq('id', list.owner).single();
        if (res.error) {
          throw res.error;
        }

        return {
          ...list,
          username: res.data.username,
          userPhoto: await getProfilePhoto(res.data.id),
        };
      }

      return list;
    })
  );

  return listsWithOwnerData;
}

/**
 * Fetch tag names from the `tags` table by category (e.g. "home filter NY", "type LA").
 */
export async function getTagsByCategory(category) {
  const { data, error } = await supabase
    .from('tags')
    .select('name')
    .eq('category', category)
    .order('name', { ascending: true });

  if (error) {
    console.error(`Error fetching tags for category "${category}":`, error);
    return [];
  }

  return (data ?? []).map(row => row.name);
}

export async function getTypeTagsForCity(city) {
  const category = `type ${cityTagSuffix(city)}`;
  return getTagsByCategory(category);
}

export async function getHomeFilterTagsForCity(city) {
  const category = `home filter ${cityTagSuffix(city)}`;
  return getTagsByCategory(category);
}

export async function getHomeBanners(city) {
  const { data, error } = await supabase
    .from('home_banner')
    .select('id, title, subtitle, is_displayed, is_modal, link_out, description, city')
    .eq('is_displayed', true)
    .order('id', { ascending: true });
  if (error) throw error;
  return (data ?? []).filter(banner => bannerMatchesCity(banner, city));
}
